import { useCallback, useContext, useEffect, useState } from "react";

import { ApiError, ServerApiContext } from "../../../../server-api";
import {
  DefaultAvailabilityEvent,
  DefaultAvailabilityWeekDayName,
  getCurrentDefaultAvailabilitySettings,
  saveDefaultAvailability as saveDefaultAvailabilityRequest,
} from "./defaultAvailabilityApi";

const useDefaultAvailability = () => {
  const [defaultAvailability, setDefaultAvailability] = useState<DefaultAvailabilityEvent[]>([]);
  const api = useContext(ServerApiContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);

  const sortAvailabilityFromMonToSun = (availability: DefaultAvailabilityEvent[]) => {
    const daysOfTheWeekInOrder: DefaultAvailabilityWeekDayName[] = [
      "MON",
      "TUE",
      "WED",
      "THU",
      "FRI",
      "SAT",
      "SUN",
    ];
    const sortedAvailability = [...availability];
    sortedAvailability.sort(
      (a, b) => daysOfTheWeekInOrder.indexOf(a.name) - daysOfTheWeekInOrder.indexOf(b.name)
    );
    return sortedAvailability;
  };

  const fetchDefaultAvailability = useCallback(async () => {
    setIsLoading(true);
    try {
      const availabilityEventsDTO = await getCurrentDefaultAvailabilitySettings(api);
      const sortedAvailability = sortAvailabilityFromMonToSun(availabilityEventsDTO.events);
      setDefaultAvailability(sortedAvailability);
    } catch (error) {
      setError(error as ApiError);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  const saveDefaultAvailability = async (
    updatedDefaultAvailabilityItem: DefaultAvailabilityEvent
  ) => {
    setIsLoading(true);
    try {
      const { id: newAvailabilityItemId } = await saveDefaultAvailabilityRequest(
        api,
        updatedDefaultAvailabilityItem
      );

      const updatedDefaultAvailability = defaultAvailability.map((availabilityItem) => {
        if (availabilityItem.id === updatedDefaultAvailabilityItem.id) {
          return {
            ...updatedDefaultAvailabilityItem,
            id: newAvailabilityItemId,
          };
        } else {
          return availabilityItem;
        }
      });

      setDefaultAvailability(updatedDefaultAvailability);
    } catch (error) {
      setError(error as ApiError);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDefaultAvailability();
  }, [fetchDefaultAvailability]);

  return { defaultAvailability, saveDefaultAvailability, isLoading, error };
};

export default useDefaultAvailability;
